import { endpoints, oids } from "../../../../configs/Settings";
import { HTTPResponseStatus } from "../../../../utils/enum";

import { useAuth } from "../../../../utils/useAuth";
import { useAxios } from "../../../../utils/useAxios";
import { auxiliarData } from "../../AuxiliarData";
import { ITaskData } from "@/utils/interfaces";
import { PostTask } from "../../task/PostTask";
import { PutMedicationRequest } from "./PutMedicationRequest";

/** Monta o resource FHIR reutilizando o builder do Bundle (shape idêntico ao POST). */
function buildMedicationResource(
  el: any,
  encounter?: any,
  source?: any
): any {
  const bundle = JSON.parse(
    auxiliarData.MedicationRequest([el], encounter, source)
  );
  return {
    ...bundle?.entry?.[0]?.resource,
    id: el.medicationRequestId,
  };
}

/**
 * Persistência de MedicationRequest:
 * - itens novos (sem id) → POST Bundle transaction (como sempre)
 * - itens editados (com medicationRequestId) → PUT individual em paralelo
 * Mantém groupIdentifier do lote (hidratado) para não quebrar o agrupamento do PDF.
 */
export async function PostMedicationRequest(
  objData: any,
  encounter?: any,
  source?: any
) {
  const list = Array.isArray(objData) ? objData : [];

  // Reusa groupIdentifier do lote (edição); itens novos no mesmo lote herdam o grupo
  const sharedGroup = list.find(
    (el: any) => el?.groupIdentifier?.value
  )?.groupIdentifier;

  const items = list.map((el: any) => ({
    ...el,
    ...(sharedGroup && !el?.groupIdentifier
      ? { groupIdentifier: sharedGroup }
      : {}),
  }));

  const toCreate = items.filter((el: any) => !el?.medicationRequestId);
  const toUpdate = items.filter((el: any) => el?.medicationRequestId);

  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
  };

  // POST Bundle (só criação) + PUTs individuais em paralelo — sem cascata
  const [createResponse] = await Promise.all([
    toCreate.length
      ? useAxios
          .post(
            `${endpoints.EHRPOSTER}/`,
            auxiliarData.MedicationRequest(toCreate, encounter, source),
            { headers, timeout: 20000 }
          )
          .then((response) => {
            if (
              response.status !== HTTPResponseStatus.Created &&
              response.status !== HTTPResponseStatus.Success
            ) {
              throw new Error(`PostMedicationRequest: ${response.status}`);
            }
            return response.data;
          })
          .catch((error) => {
            console.error("PostMedicationRequest ", error);
            throw new Error();
          })
      : Promise.resolve(null),
    toUpdate.length
      ? Promise.all(
          toUpdate.map(async (el: any) => {
            const resource = buildMedicationResource(el, encounter, source);
            await PutMedicationRequest(resource, el.medicationRequestId);
            return el.medicationRequestId as string;
          })
        )
      : Promise.resolve([]),
  ]);

  // Task só em receita nova (há itens sem id no lote)
  if (toCreate.length && encounter?.requester) {
    const taskData: ITaskData = {
      status: "requested",
      intent: "order",
      priority: "routine",
      description: `O médico ${encounter.requester.display} receitou o medicamento ${toCreate?.[0]?.name}`,

      owner: encounter?.requester?.reference,
      for: `Patient/${oids.cpf}-${encounter?.subject}`,
    };

    PostTask(taskData);
  }

  // Resposta Bundle-like alinhada à ordem de `items` (Footer/basedOn)
  let createEntryCursor = 0;
  const entry = items.map((el: any) => {
    if (el?.medicationRequestId) {
      return {
        response: {
          status: "200 OK",
          location: `MedicationRequest/${el.medicationRequestId}`,
        },
      };
    }
    const created = createResponse?.entry?.[createEntryCursor++];
    return (
      created ?? {
        response: { status: "201 Created" },
      }
    );
  });

  return {
    resourceType: "Bundle",
    type: "transaction-response",
    entry,
  };
}
